import * as React from 'react';

import { StatsigProvider } from '@coursera-web/statsig';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import connectToStores from 'js/lib/connectToStores';

import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import InteractionGuard from 'bundles/unified-common/components/InteractionGuard';
import {
  GlobalCoachChatStateProvider,
  useGlobalCoachChatState,
} from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import NavAiSparkleExperimentProvider, {
  useNavAiSparkle,
} from 'bundles/unified-common/providers/NavAiSparkleExperimentProvider';

import 'css!bundles/collections/styl/index';

const GlobalCoachChat = React.lazy(
  () => import('bundles/unified-common/components/coach-on-discovery/GlobalCoachChat')
);

type Props = {
  requestCountryCode?: string;
  csrfToken?: string;
  children?: JSX.Element;
};

const CollectionsApp = ({ children }: Props) => {
  const { showNavAiSparkle } = useNavAiSparkle();
  const { coachOpen, pageChatType } = useGlobalCoachChatState();
  const isFullscreenCoachChatOpen = coachOpen && pageChatType === null;

  return (
    <div className="rc-CollectionsApp">
      <UniversalPageFeatures />
      <DefaultHeader showMetaNav />
      {showNavAiSparkle && (
        <React.Suspense fallback={null}>
          <GlobalCoachChat />
        </React.Suspense>
      )}
      <InteractionGuard preventInteraction={isFullscreenCoachChatOpen}>
        {children}
        <PageFooter />
      </InteractionGuard>
    </div>
  );
};

const CollectionsAppWithProvider = ({ children, ...props }: Props) => {
  return (
    <StatsigProvider>
      <GlobalCoachChatStateProvider>
        <NavAiSparkleExperimentProvider>
          <CollectionsApp {...props}>{children}</CollectionsApp>
        </NavAiSparkleExperimentProvider>
      </GlobalCoachChatStateProvider>
    </StatsigProvider>
  );
};
export default compose<{}, Props>(
  connectToStores<Props, Omit<Props, 'csrfToken' | 'requestCountryCode'>>(
    [ApplicationStoreClass],
    (ApplicationStore, props) => ({
      ...props,
      csrfToken: ApplicationStore.getState().csrfToken,
      requestCountryCode: ApplicationStore.getState().requestCountryCode,
    })
  ),
  Retracked.createTrackedContainer(() => {
    return {
      namespace: {
        page: 'collections_page',
        app: 'collections',
      },
    };
  })
)(CollectionsAppWithProvider);
